import { getRemainingQty } from '@/api/daily-stock-api';
import { getOrdersForSale, saleDateToDay } from '@/api/order-api';
import { toast } from '@/hooks/use-toast';
import { useWebSocketChannel } from '@/hooks/useWebSocketChannel';
import { getOrCreateDeviceId } from '@/lib/device-id';
import { OrderResponse } from '@/types/order-type';
import { WsEvent } from '@/types/ws-type';
import { useCallback, useEffect, useRef, useState } from 'react';
import { OrderWsEvent } from '../OrderInlineAlert';
import { parseWsEvent } from './pinned-board-utils';

type UseVariantOrdersOptions = {
  variantId: string;
  initialRemain: number;
  orderDate?: string;
};

export function useVariantOrders({
  variantId,
  initialRemain,
  orderDate = saleDateToDay(),
}: UseVariantOrdersOptions) {
  const [remain, setRemain] = useState(initialRemain);
  const [orderForVariants, setOrderForVariants] = useState<OrderResponse[]>([]);
  const [lastOrderEvent, setLastOrderEvent] = useState<OrderWsEvent | null>(null);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const isCreatingOrderRef = useRef(false);

  const fetchVariantRemain = useCallback(async () => {
    const resp = await getRemainingQty(variantId);
    if (resp.success) {
      setRemain(resp.data.stockInHand || 0);
    } else {
      toast({
        title: 'Error',
        description: `Error fetching remaining qty for variant id : ${variantId}`,
        variant: 'success',
      });
    }
  }, [variantId]);

  const fetchOrderForVariant = useCallback(async () => {
    const resp = await getOrdersForSale(orderDate, variantId);
    if (resp.success) {
      setOrderForVariants(resp.data.content);
    } else {
      toast({
        title: 'Error',
        description: resp.message || `Error fetching order for variant : ${variantId}`,
        variant: 'destructive',
      });
    }
  }, [orderDate, variantId]);

  const refreshOrdersAndRemain = useCallback(async () => {
    await fetchOrderForVariant();
    await fetchVariantRemain();
  }, [fetchOrderForVariant, fetchVariantRemain]);

  useEffect(() => {
    setRemain(initialRemain);
    setOrderForVariants([]);
    setLastOrderEvent(null);
    setNewOrderCount(0);
    void fetchOrderForVariant();
    void fetchVariantRemain();
  }, [variantId, orderDate, fetchOrderForVariant, fetchVariantRemain, initialRemain]);

  const handleOrderWsEvent = useCallback(
    async (event: WsEvent) => {
      if (event.type !== 'NEW_ORDER' && event.type !== 'ORDER_UPDATED') return;

      const orderEvent = event as OrderWsEvent;
      const isOwnDevice = orderEvent.deviceId === getOrCreateDeviceId();

      if (!isOwnDevice) {
        setLastOrderEvent(orderEvent);
        if (event.type === 'NEW_ORDER') {
          setNewOrderCount((prev) => prev + 1);
        }
      } else if (isCreatingOrderRef.current) {
        return;
      }

      await refreshOrdersAndRemain();
    },
    [refreshOrdersAndRemain],
  );

  const handleOrderWsMessage = useCallback(
    (stompMsg: { body: string }) => {
      const event = parseWsEvent(stompMsg.body);
      if (event) void handleOrderWsEvent(event);
    },
    [handleOrderWsEvent],
  );

  const handleInventoryWsMessage = useCallback(
    (stompMsg: { body: string }) => {
      const event = parseWsEvent(stompMsg.body);
      if (event?.type === 'INVENTORY_MOVEMENT_UPDATED' && event.payload === variantId) {
        void refreshOrdersAndRemain();
      }
    },
    [refreshOrdersAndRemain, variantId],
  );

  useWebSocketChannel(
    `/topic/orders/${variantId}`,
    handleOrderWsMessage,
    `pinned-variant-orders-${variantId}`,
  );
  useWebSocketChannel(
    '/topic/orders',
    handleOrderWsMessage,
    `pinned-variant-global-orders-${variantId}`,
  );
  useWebSocketChannel('/topic/inventory-update', handleInventoryWsMessage);

  return {
    remain,
    setRemain,
    orderForVariants,
    setOrderForVariants,
    lastOrderEvent,
    newOrderCount,
    isCreatingOrderRef,
    fetchOrderForVariant,
    fetchVariantRemain,
    refreshOrdersAndRemain,
  };
}
